import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import { puedeUsarAplicarDescuentos } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader } from 'components/financepro';

export { puedeUsarAplicarDescuentos };

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: FP.color.navy },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: FP.color.navy },
};

const AplicarDescuentos = ({ open = false, onClose, embedded = false }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles } = controller;
  const { post } = useApi();

  const puedeUsar = useMemo(() => puedeUsarAplicarDescuentos(roleDetalles), [roleDetalles]);
  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const [procesando, setProcesando] = useState(false);
  const [aplicarPrestamo, setAplicarPrestamo] = useState(true);

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany],
  );

  const cerrar = () => {
    if (procesando) return;
    if (onClose) onClose();
  };

  const procesar = async () => {
    if (!puedeUsar) {
      showError('No tiene permisos para aplicar descuentos');
      return;
    }
    setProcesando(true);
    try {
      const { ok, data } = await post('/api/nomina_aplicardescuentos', {
        ...credencialesEmpresa(),
        aplicar_prestamo: aplicarPrestamo ? '1' : '0',
      });
      if (!ok) throw new Error(data.Error || 'No se pueden aplicar los descuentos');
      showSuccess(data.ok || 'Descuentos aplicados');
      if (onClose) onClose();
    } catch (e) {
      showError(e.message || 'No se pueden aplicar los descuentos');
    } finally {
      setProcesando(false);
    }
  };

  const cuerpo = !puedeOperar ? (
    <Typography sx={{ color: FP.color.textSecondary, py: 2 }}>
      Seleccione una empresa para aplicar descuentos.
    </Typography>
  ) : (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem', mb: 2.5 }}>
        Seleccione los descuentos que desea aplicar a todos los empleados de la nómina activa.
      </Typography>

      <FormControlLabel
        control={<Switch checked disabled sx={switchSx} />}
        label="Aplicar Seguro Familiar de Salud (SFS)"
        sx={{ display: 'flex', mb: 1.5, '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: '0.9rem' } }}
      />
      <FormControlLabel
        control={<Switch checked disabled sx={switchSx} />}
        label="Aplicar ISR"
        sx={{ display: 'flex', mb: 1.5, '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: '0.9rem' } }}
      />
      <FormControlLabel
        control={<Switch checked disabled sx={switchSx} />}
        label="Aplicar AFP"
        sx={{ display: 'flex', mb: 1.5, '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: '0.9rem' } }}
      />
      <FormControlLabel
        control={
          <Switch
            checked={aplicarPrestamo}
            onChange={(e) => setAplicarPrestamo(e.target.checked)}
            sx={switchSx}
          />
        }
        label="Aplicar Préstamos"
        sx={{ display: 'flex', mb: 1, '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: '0.9rem' } }}
      />

      <Typography sx={{ color: FP.color.textMuted, fontSize: '0.82rem', mt: 1.5 }}>
        SFS, ISR y AFP se calculan automáticamente al iniciar la nómina. Active «Préstamos» para
        descontar la cuota vigente de cada empleado.
      </Typography>
    </Box>
  );

  const acciones = (
    <>
      {onClose && (
        <Button onClick={cerrar} disabled={procesando} sx={btnOutline(FP.color.textSecondary)}>
          Cancelar
        </Button>
      )}
      <Button
        onClick={procesar}
        disabled={procesando || !puedeUsar || !puedeOperar}
        startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : <PlayArrowOutlinedIcon />}
        sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
      >
        {procesando ? 'Procesando…' : 'Procesar'}
      </Button>
    </>
  );

  if (!puedeUsar) return null;

  if (embedded) {
    return (
      <Box>
        {cuerpo}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>{acciones}</Box>
      </Box>
    );
  }

  return (
    <Dialog
      open={Boolean(open)}
      onClose={cerrar}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Aplicar Descuentos</DialogTitle>
      <DialogContent>{cuerpo}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>{acciones}</DialogActions>
    </Dialog>
  );
};

export function AplicarDescuentosPage() {
  const { controller } = useProductosMaterialContext();
  const { roleDetalles, nombreNegocio } = controller;
  const puedeUsar = useMemo(() => puedeUsarAplicarDescuentos(roleDetalles), [roleDetalles]);

  if (!puedeUsar) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <PageHeader title="Aplicar Descuentos" subtitle="Nómina" />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <LockOutlinedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para aplicar descuentos.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Aplicar Descuentos"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina` : 'Nómina'}
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Card sx={cardShellSx}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <AplicarDescuentos embedded open />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default AplicarDescuentos;
